import {
  renderBotMessage,
  type BotMessageContent,
  type MessageLocale,
} from './bot-messages';
import { z } from 'zod';
import { uuidV7Schema } from './ids';

export const turnActivitySchema = z.enum([
  'reply',
  'catalog',
  'calendar',
  'confirmed_request',
]);
export type TurnActivity = z.infer<typeof turnActivitySchema>;
export const turnProgressSchema = z
  .object({
    eventId: uuidV7Schema,
    phase: z.enum([
      'received',
      'working',
      'delayed',
      'recovering',
      'completed',
      'failed',
    ]),
    activity: turnActivitySchema,
    startedAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
  })
  .strict();
export type TurnProgress = z.infer<typeof turnProgressSchema>;

export function customerTurnProgress(
  progress: TurnProgress,
  now = Date.now()
): TurnProgress {
  return ['received', 'working'].includes(progress.phase) &&
    now - Date.parse(progress.startedAt) >= 5_000
    ? { ...progress, phase: 'delayed' }
    : progress;
}

export function isTurnStatusQuestion(text: string): boolean {
  return /^(?:hola|(?:hola[, ]*)?(?:sigues ahi|estas ahi|hay alguien|me lees|me escuchas|are you (?:still )?there|still there|hello))[?.!¿¡\s]*$/u.test(
    text
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .toLowerCase()
      .replace(/^[¿¡\s]+/u, '')
  );
}

export const customerTurnProgressSchema = turnProgressSchema.extend({
  message: z.string().max(1000),
});
export type CustomerTurnProgress = z.infer<typeof customerTurnProgressSchema>;

export function renderTurnProgress(
  progress: TurnProgress,
  messages: BotMessageContent,
  locale: MessageLocale,
  now = Date.now()
): CustomerTurnProgress {
  const current = customerTurnProgress(progress, now);
  return {
    ...current,
    message:
      current.phase === 'completed'
        ? ''
        : renderBotMessage(
            messages,
            current.phase === 'failed'
              ? 'progress_failed'
              : current.phase === 'recovering'
                ? 'status_retrying'
                : `progress_${current.activity}`,
            locale,
            current.eventId
          ),
  };
}
